import React, { useContext, useState } from "react";
import { Box, Flex, Image } from "theme-ui";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import TextInput from "components/forms/TextInput";
import BasicButton from "components/forms/BasicButton";
import { CheckMarkIcon } from "components/svg";
import AvatarSquareIcon from "components/svg/icons/AvatarSquareIcon";
import LogoUpload from "components/ui/Header/LogoUpload";
import { useToast } from "components/toast";
import UserContext from "context/User";
import { updateProfile } from "api/Users";

const UserSchema = Yup.object()
  .shape({
    email: Yup.string().email("Invalid email").required("Email is required."),
    firstName: Yup.string().required("First Name is required."),
    lastName: Yup.string().required("Last Name is required."),
  })
  .noUnknown(true);

export default function ProfileSettings() {
  const { user, setUser } = useContext<any>(UserContext);
  const toast = useToast();

  const handleSubmit = async (values: any, actions: any) => {
    const update = {
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
      // What goes on documents this user sends (proposals).
      jobTitle: values.jobTitle ? String(values.jobTitle).trim() : null,
      phone: values.phone ? String(values.phone).trim() : null,
      data: {
        ...(!values?.data?.logo && user?.data?.logo ? { logo: "" } : {}),
      },
    };

    const res = await updateProfile({ update });
    if (res?.id) {
      toast.add("Profile updated.", "var(--theme-ui-colors-success)");
      setUser(res);
    } else {
      toast.add(res?.message || "Error updating profile", "var(--theme-ui-colors-danger)");
    }
  };

  if (!user) return null;

  return (
    <Box sx={{ paddingLeft: "30px", paddingRight: "30px" }}>
      <Box sx={{ fontFamily: "Poppins, sans-serif", fontSize: "16px", fontWeight: 600, color: "base_800", marginBottom: "4px" }}>
        Profile
      </Box>
      <Box sx={{ fontFamily: "Poppins, sans-serif", fontSize: "13px", color: "base_500", marginBottom: "24px" }}>
        Update your name and profile photo.
      </Box>

      <Formik
        initialValues={{ ...user }}
        onSubmit={handleSubmit}
        validationSchema={UserSchema}
        enableReinitialize
      >
        {({ isSubmitting, errors, values, submitCount, setFieldValue }: any) => (
          <Form noValidate autoComplete="off">
            <Box sx={{ maxWidth: "400px" }}>
              <TextInput
                name="firstName"
                label="First Name"
                value={values.firstName}
                onChange={(val: any) => setFieldValue("firstName", val)}
                $errors={errors.firstName && submitCount > 0 ? errors.firstName : null}
              />
              <TextInput
                name="lastName"
                label="Last Name"
                value={values.lastName}
                onChange={(val: any) => setFieldValue("lastName", val)}
                $errors={errors.lastName && submitCount > 0 ? errors.lastName : null}
              />
              <TextInput
                name="jobTitle"
                label="Job title (shown on proposals you send)"
                value={values.jobTitle ?? ""}
                onChange={(val: any) => setFieldValue("jobTitle", val)}
              />
              <TextInput
                name="phone"
                label="Phone (shown on proposals you send)"
                value={values.phone ?? ""}
                onChange={(val: any) => setFieldValue("phone", val)}
              />
            </Box>

            <Flex sx={{ marginTop: "20px" }}>
              <label style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 12, color: "#818181", marginBottom: 8 }}>Profile Photo</label>
            </Flex>
            <Flex sx={{ gap: 20, alignItems: "center", marginBottom: "30px" }}>
              <Flex sx={{ flexDirection: "column" }}>
                {values?.data?.logo && <Image src={values?.data?.logo} width={60} style={{ borderRadius: 6, marginRight: 10 }} />}
                {!values?.data?.logo && AvatarSquareIcon({ width: 60, height: 60, fill: "#BFBFBF" })}
              </Flex>
              <LogoUpload user={user} title={values?.data?.logo ? "Replace Photo" : "Upload Photo"} afterUpload={(e: any) => setUser(e)} />
              {values?.data?.logo && (
                <BasicButton scheme="dangerSecondary" onClick={() => setFieldValue("data.logo", "")}>
                  Remove
                </BasicButton>
              )}
            </Flex>

            <BasicButton
              type="submit"
              $submitting={isSubmitting}
              $iconHeight="14px"
              $loadingColor="#fff"
              keepText={true}
            >
              {!isSubmitting && <CheckMarkIcon fill={"#fff"} />}
              Save Profile
            </BasicButton>
          </Form>
        )}
      </Formik>
    </Box>
  );
}
